import type { IconProps } from '@sr/ui-library';
import {
  Icon,
  SR_ICONS,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  cn,
} from '@sr/ui-library';
import type { ReactNode } from 'react';
import { useEffect, useId, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { SIDEBAR_LINK_CLASS } from './SidebarLink';

export interface SidebarLinkGroupProps {
  /** Child SidebarLink elements */
  children: ReactNode;
  icon: IconProps['icon'];
  label: string;
  /** Path segments that, when present in the current URL, auto-expand the group */
  matchPaths?: string[];
}

/**
 * Collapsible navigation group for the MSR3 sidebar.
 *
 * Renders a button header that expands/collapses a list of child links.
 * Auto-expands when the current route matches any of the provided `matchPaths`,
 * but respects a manual collapse — `isOpen` alone drives visibility so the user
 * can collapse the group even while on a child route.
 */
export const SidebarLinkGroup = ({
  children,
  icon,
  label,
  matchPaths = [],
}: SidebarLinkGroupProps) => {
  const location = useLocation();

  // Match on whole, slash-bounded path segments so a matchPath like
  // "/disbursements" doesn't also match unrelated routes like "/old-disbursements".
  const currentPath = `${location.pathname}/`;
  const isRouteMatch = matchPaths.some((path) => {
    const segment = `/${path.replace(/^\/+|\/+$/g, '')}/`;

    return currentPath.includes(segment);
  });

  const [isOpen, setIsOpen] = useState(isRouteMatch);

  const subListId = useId();

  // Auto-open when navigating into a matching child route, but allow manual
  // collapse once there — isOpen alone drives visibility.
  useEffect(() => {
    if (isRouteMatch) setIsOpen(true);
  }, [isRouteMatch]);

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        aria-controls={subListId}
        aria-current={isRouteMatch ? 'page' : undefined}
        aria-expanded={isOpen}
        className={cn(SIDEBAR_LINK_CLASS, 'text-sm font-light')}
        data-sidebar-link
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        {/* Red left-edge indicator, lit while on a child route so the group
            header reflects the active section like a top-level link would. */}
        <span
          className={cn(
            'absolute left-0 top-1/2 h-4 w-1 -translate-y-1/2 rounded-full bg-primary transition-opacity',
            'opacity-0 group-hover/link:opacity-30 group-aria-[current=page]/link:opacity-100',
          )}
        />

        <Icon
          className="mr-1 shrink-0 text-foreground/50 transition-colors group-hover/link:text-foreground group-aria-[current=page]/link:text-foreground"
          icon={icon}
          size="sm"
        />
        <span className="flex-1 text-left text-foreground/70 group-hover/link:text-foreground group-aria-[current=page]/link:text-foreground">
          {label}
        </span>
        <Icon
          className={cn(
            'shrink-0 text-foreground/40 transition-transform duration-200',
            isOpen && 'rotate-90',
          )}
          icon={SR_ICONS.chevronRight}
          size="sm"
        />
      </SidebarMenuButton>

      {isOpen && (
        <SidebarMenuSub
          id={subListId}
          className="ml-4 mr-0 mt-1 translate-x-0 gap-0 border-border py-0 pl-2 pr-0"
        >
          {children}
        </SidebarMenuSub>
      )}
    </SidebarMenuItem>
  );
};
